import { useCallback, useEffect, useState } from 'react';
import { seitenaufrufeAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, Download, Loader2, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/* Seitenaufrufe einer Landingpage — anonyme Reichweitenmessung.
   "Sitzungen" ist die Zahl, die zaehlt: eindeutige Besuche. "Aufrufe"
   enthaelt auch das erneute Oeffnen derselben Sitzung. */

const SEITEN = [
  // 12.10.2026: Die Landingpage zählt seit 26.09.2026 mit (landingpage/js/main.js), stand aber nicht in der Auswahl
  { slug: 'landingpage', label: 'Landingpage Probetraining' },
  { slug: 'coaching', label: 'Angebotsseite P1 Coaching' },
  { slug: 'seminar', label: 'Seminar' },
  { slug: 'funnel', label: 'Funnel Erwachsene' },
  { slug: 'funnelkids', label: 'Funnel Kids' },
  { slug: '10wochen', label: 'Kids Launch (/10wochen)' },
  { slug: 'gym-fights', label: 'Gym Fights (/gym-fights)' },
];

const label = (slug) => SEITEN.find((s) => s.slug === slug)?.label || slug;

export default function SeitenaufrufeKarte() {
  const { userPermissions, isAdminPermissions } = useAuth();
  // Zuruecksetzen loescht unwiderruflich — dieselbe Berechtigung wie im Backend.
  const darfLeeren = isAdminPermissions || !!userPermissions?.['settings.edit_general'];

  const [seite, setSeite] = useState('seminar');
  const [tage, setTage] = useState(30);
  const [daten, setDaten] = useState(null);
  const [lade, setLade] = useState(true);
  const [fragtNach, setFragtNach] = useState(false);
  const [leert, setLeert] = useState(false);

  const laden = useCallback(async () => {
    setLade(true);
    try {
      const r = await seitenaufrufeAPI.uebersicht(seite, tage);
      setDaten(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seitenaufrufe konnten nicht geladen werden');
      setDaten(null);
    }
    setLade(false);
  }, [seite, tage]);
  useEffect(() => { laden(); }, [laden]);

  const csv = async () => {
    try {
      const r = await seitenaufrufeAPI.exportCSV(seite, tage);
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = Object.assign(document.createElement('a'),
        { href: url, download: `seitenaufrufe_${seite}_${tage}tage.csv` });
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Export fehlgeschlagen'); }
  };

  const zuruecksetzen = async () => {
    setLeert(true);
    try {
      const r = await seitenaufrufeAPI.zuruecksetzen(seite);
      toast.success(`${r.data.geloescht} Aufrufe gelöscht — ${label(seite)} zählt wieder ab null.`);
      setFragtNach(false);
      laden();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Zurücksetzen fehlgeschlagen');
    }
    setLeert(false);
  };

  // Ein Seitenwechsel darf die offene Sicherheitsabfrage nicht stehen lassen —
  // sonst leert man am Ende die falsche Seite.
  useEffect(() => { setFragtNach(false); }, [seite]);

  const maxAufrufe = Math.max(1, ...(daten?.pro_tag || []).map((d) => d.aufrufe));

  return (
    <Card data-testid="seitenaufrufe">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-2"><Eye className="h-4 w-4" /> Seitenaufrufe</span>
          <span className="flex items-center gap-2">
            <select value={seite} onChange={(e) => setSeite(e.target.value)}
                    className="text-sm font-normal rounded-md border border-border bg-background px-2 py-1">
              {SEITEN.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
            </select>
            <select value={tage} onChange={(e) => setTage(Number(e.target.value))}
                    className="text-sm font-normal rounded-md border border-border bg-background px-2 py-1">
              <option value={7}>7 Tage</option>
              <option value={30}>30 Tage</option>
              <option value={90}>90 Tage</option>
            </select>
            <Button size="sm" variant="outline" className="gap-1" onClick={csv}>
              <Download className="h-4 w-4" /> CSV
            </Button>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {lade && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}

        {!lade && daten && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <p className="text-2xl font-bold leading-none">{daten.sitzungen_gesamt}</p>
                <p className="text-xs text-muted-foreground mt-1">eindeutige Besuche</p>
              </div>
              <div>
                <p className="text-2xl font-bold leading-none">{daten.aufrufe_gesamt}</p>
                <p className="text-xs text-muted-foreground mt-1">Aufrufe gesamt</p>
              </div>
              {daten.buchungen !== undefined && (
                <>
                  <div>
                    <p className="text-2xl font-bold leading-none">{daten.buchungen}</p>
                    <p className="text-xs text-muted-foreground mt-1">Anmeldungen</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold leading-none text-primary">
                      {daten.conversion_prozent} %
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">von Besuch zu Anmeldung</p>
                  </div>
                </>
              )}
            </div>

            {daten.pro_tag?.length > 0 ? (
              <div>
                <p className="text-xs font-medium mb-2">Verlauf</p>
                <div className="flex items-end gap-1 h-20">
                  {daten.pro_tag.map((d) => (
                    <div key={d.tag} className="flex-1 flex flex-col items-center gap-1 min-w-0"
                         title={`${d.tag}: ${d.aufrufe} Aufrufe, ${d.sitzungen} Besuche`}>
                      <div className="w-full rounded-t bg-primary/70"
                           style={{ height: `${(d.aufrufe / maxAufrufe) * 100}%`, minHeight: 2 }} />
                      <span className="text-[9px] text-muted-foreground">{d.tag.slice(8)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Noch keine Aufrufe im gewählten Zeitraum — sobald jemand den Link öffnet,
                erscheint er hier.
              </p>
            )}

            {daten.quellen?.length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1">Woher die Besucher kommen</p>
                <table className="text-sm w-full max-w-md">
                  <tbody>
                    {daten.quellen.slice(0, 8).map((q, i) => (
                      <tr key={i} className="border-b border-border/40">
                        <td className="py-1 pr-2">{q.quelle}</td>
                        <td className="py-1 pr-2 text-muted-foreground">{q.referrer_host || '—'}</td>
                        <td className="py-1 text-right font-mono">{q.aufrufe}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-end justify-between gap-3 flex-wrap">
              <p className="text-[11px] text-muted-foreground max-w-md">
                Anonyme Zählung ohne Cookies und ohne IP-Adressen; die Daten löschen sich
                nach 180 Tagen selbst.
              </p>

              {darfLeeren && !fragtNach && (
                <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground"
                        onClick={() => setFragtNach(true)}>
                  <RotateCcw className="h-3.5 w-3.5" /> Zähler zurücksetzen
                </Button>
              )}

              {darfLeeren && fragtNach && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">
                    Alle Aufrufe von „{label(seite)}" löschen — auch die außerhalb
                    des gewählten Zeitraums?
                  </span>
                  <Button size="sm" variant="destructive" onClick={zuruecksetzen} disabled={leert}>
                    {leert ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Ja, löschen'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setFragtNach(false)} disabled={leert}>
                    Abbrechen
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
